import { FormProvider, SubmitErrorHandler, SubmitHandler, useForm } from 'react-hook-form'
import { ButtonComponent } from '@syncfusion/ej2-react-buttons'
import { useEffect, useState } from 'react'
import { createSpinner, hideSpinner, showSpinner } from '@syncfusion/ej2-react-popups'
import styles from '../../styles/components/register.module.scss'
import '../../styles/general/forms.scss'
import { useAppDispatch, useAppSelector } from '../../hooks/reduxHooks.tsx'
import { appRoles, protectedResources } from '../../global/config.tsx'
import { useOidcAccessToken, useOidcUser } from '@axa-fr/react-oidc'

import { fetchDataFromApi } from '../../services/fetchDataFromApi.tsx'
import { OidcUserExtendedInfo } from '../../types/OidcUserExtendedInfo.tsx'

import { CommunicationData } from './components/CommunicationData.tsx'
import { GeneralData } from './components/GeneralData.tsx'
import { AddressData } from './components/AddressData.tsx'
import { QualificationsData } from './components/QualificationsData.tsx'
import { addRegistrationToList } from '../../redux/registerSlice.tsx'

import { MenuHeaderBar } from '../../components/global/MenuHeaderBar.tsx'
import { NwLecturerApplicationDto } from '../../types/NwLecturerApplicationDto.tsx'

import RegistrationStatusMsg from './components/RegistrationStatusMsg.tsx'
import useToast from '../../components/global/NotificationToast.tsx'
import { useParams } from 'react-router'
import { VersionSelectDd } from './components/VersionSelectDd.tsx'
import TopicSelection from './components/TopicSelection.tsx'
import useUserRoles from '../../hooks/useUserRoles.tsx'

const Register = () => {
  const selectedRegIdx = useAppSelector((state) => state.register.selectedIdx)
  const registerVersions = useAppSelector((state) => state.register.registrations)

  const registerForm = useForm<NwLecturerApplicationDto>({ mode: 'onSubmit', reValidateMode: 'onSubmit' })
  const {
    handleSubmit,
    reset,
    formState: { errors },
  } = registerForm

  const [spinnerTarget, setSpinnerTarget] = useState<HTMLElement>()

  const [newAccountMessage, setNewAccountMessage] = useState(false)

  const { showToast } = useToast()
  const { sub: subFromParams } = useParams()

  const { accessToken } = useOidcAccessToken()
  const { oidcUser } = useOidcUser<OidcUserExtendedInfo>()
  const userRoles = oidcUser?.role
  const roleIntern = [appRoles.Intern]

  const { isInternal } = useUserRoles()

  const dispatch = useAppDispatch()

  useEffect(() => {
    setSpinnerTarget(document.getElementsByClassName('nw-app')[0] as HTMLElement)
    if (spinnerTarget)
      createSpinner({
        target: spinnerTarget,
      })
  }, [])

  useEffect(() => {
    if (registerVersions.length < 1) {
      setNewAccountMessage(true)
    }
  }, [registerVersions])

  // Bei wechsel des Versions-Dropdown im Header, resette Werte entsprechend dem selektierten Index
  useEffect(() => {
    // version mit übergebenen index:
    const newSelectedVersionData = registerVersions.find((version) => version.actualIndex === selectedRegIdx)

    if (newSelectedVersionData) {
      reset({ ...newSelectedVersionData })
    }
  }, [selectedRegIdx])

  // Setze sub in form wenn per url param übergeben, zb wechsel von DozList auf Profil
  useEffect(() => {
    if (subFromParams) {
      reset({ sub: subFromParams })
    }
  }, [reset, subFromParams])

  // Todo: entfernen, bis auf readFiles, sub aus Profil/Redux holen
  // useEffect(() => {
  //   if (oidcUser && oidcUser.sub) {
  //     if (spinnerTarget) {
  //       showSpinner(spinnerTarget)
  //     }
  //
  //     if (accessToken && profile) {
  //       const readProfileApiUrl = `${protectedResources.apiList.readProfile}?id=${profile.sub}${profile.adrId ? `&docentId=${profile.adrId}` : ''}`
  //       // Read Profile
  //       fetchDataFromApi(readProfileApiUrl, accessToken, 'GET').then((response) => {
  //         console.log(response)
  //         if (spinnerTarget) {
  //           hideSpinner(spinnerTarget)
  //         }
  //
  //         if (response.status === 204) {
  //           // Neuer User
  //           reset({ sub: oidcUser.sub })
  //           setNewAccountMessage(true)
  //         }
  //
  //         if (response.status === 200) {
  //           // Bereits registrierung gespeichert, Update data
  //           const profileData = response.data as NwLecturerApplicationDto[]
  //
  //           if (profileData.length === 0) {
  //             setNewAccountMessage(true)
  //           }
  //
  //           // Returned Liste mit Versionen, nehmen letzte (aktuellste)
  //           if (profileData.length > 0) {
  //             const mostRecentProfileData = profileData[profileData.length - 1]
  //             //reset({ ...mostRecentProfileData })
  //             dispatch(setRegistrations(profileData)) // alle
  //           }
  //
  //           // Hole Dateiliste inkl Profilbild
  //           const readFilesUrl = `${protectedResources.apiList.readFiles}/${oidcUser.sub}`
  //
  //           fetchDataFromApi(readFilesUrl, accessToken, 'GET').then((response) => {
  //             const responseData = response.data as NwDbFile[]
  //             dispatch(setFiles(responseData))
  //           })
  //         }
  //       })
  //     } else {
  //       setNewAccountMessage(true)
  //     }
  //   }
  // }, [oidcUser])

  const onSubmit: SubmitHandler<NwLecturerApplicationDto> = (formData) => {
    if (import.meta.env.DEV) console.log(formData)

    console.log('submitting')

    let formDataWithLastChangedBy
    if (isInternal) {
      formDataWithLastChangedBy = { ...formData, lastChangedBy: oidcUser.sub }
    } else {
      formDataWithLastChangedBy = { ...formData, lastChangedBy: oidcUser.sub, sub: oidcUser.sub }
    }

    const url = `${protectedResources.apiList.createOrUpdateProfile}`
    if (accessToken) {
      if (spinnerTarget) {
        showSpinner(spinnerTarget)
      }

      fetchDataFromApi(url, accessToken, 'POST', 'json', formDataWithLastChangedBy).then((response) => {
        if (spinnerTarget) {
          hideSpinner(spinnerTarget)
        }

        if (response.status === 200) {
          // resette Form mit den return daten des API calls
          const returnData = response.data as NwLecturerApplicationDto

          if (Object.keys(returnData).length > 0) {
            reset({ ...returnData })
            dispatch(addRegistrationToList(returnData)) // alle
          }
          setNewAccountMessage(false)
          showToast({
            content: 'Ihre Daten wurden erfolgreich übermittelt!',
            type: 'success',
          })
        } else {
          showToast({
            content: 'Bei der Übertragung ist etwas schief gelaufen. Bitte versuchen Sie es später noch einmal.',
            type: 'danger',
          })
        }
      })
    }
  }

  // Zeige Error-Meldung bei Validierungs-Fehlern
  const showValidationErrorMsg = () => {
    if (Object.keys(errors).length > 0) {
      showToast({
        title: 'Fehler',
        content: `Das Formular konnte nicht gesendet werden (${Object.keys(errors).length} Validierungsfehler). Bitte beheben Sie die Fehler und versuchen es erneut.`,
        type: 'danger',
      })
    }
  }

  // Zeige Form-Validierungsfehler bei erstmaligem Validieren
  useEffect(() => {
    showValidationErrorMsg()
  }, [errors])

  const formSubmitBtn = () => {
    return (
      <>
        <ButtonComponent isPrimary form="registerForm">
          Speichern
        </ButtonComponent>
      </>
    )
  }

  const headerContentRight = () => {
    return (
      <>
        <VersionSelectDd />
        {/* Nur rendern wenn User === intern */}
      </>
    )
  }

  const onError: SubmitErrorHandler<NwLecturerApplicationDto> = () => {
    showValidationErrorMsg()
  }

  return (
    <>
      <MenuHeaderBar headerContent={'Profil'} contentRight={formSubmitBtn} contentLeft={headerContentRight}></MenuHeaderBar>
      <div className={'nw-content scrollbarOffset'}>
        {!roleIntern.some((role) => userRoles?.includes(role)) && <RegistrationStatusMsg isNewAccount={newAccountMessage} />}

        <FormProvider {...registerForm}>
          <form onSubmit={handleSubmit(onSubmit, onError)} id="registerForm">
            <GeneralData />
            <CommunicationData />
            <AddressData />
            <QualificationsData />
            <TopicSelection sub={oidcUser && oidcUser.sub} />
            <div className={styles.formFooter}>
              <ButtonComponent isPrimary cssClass="btnLarge">
                Speichern
              </ButtonComponent>
            </div>
          </form>
        </FormProvider>
      </div>
    </>
  )
}

export default Register
